<script setup lang="ts">
import {computed, onUnmounted, ref} from "vue";
import {Dayjs} from "dayjs";
import LegacyAccess from "@/core/interfaces/Access";
import {StaffingTarget, TeamHourBalance} from "@/core/interfaces/TeamPlanning";
import {JOB_ROLES, TimeRange, UNKNOWN_ROLE_COLOR, formatHours, formatSignedHours, formatTime, jobRoleForRank, jobRoleOption, toMinutes, toTime} from "@/team/planning";
import {motifOf} from "@/planning/lib/absences";
import {DayCoverage, MemberDay, SlotShortfall, dayCoverage, dayMinutes, dayShortfalls, formatMinutes} from "@/planning/lib/weekPlanning";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";
import {MinuteRange, OpeningState, openRangesOn} from "@/agenda/lib/openingHours";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/core/components/ui/tooltip";
import {Skeleton} from "@/core/components/ui/skeleton";
import {CircleAlert, CircleCheck, Clock, Pencil, Scale, ShieldAlert, ShieldCheck, Store, UserRoundPlus} from "@lucide/vue";

/** Displayed amplitude of a day column, for bar positioning. */
const DAY_START = 8 * 60;
const DAY_END = 20 * 60;
const RESIZE_STEP = 15;

const {days, members, coverageMembers, resolveDay, targets, schedules, dayFocus, hourBalances, opening, openingState, teamLoaded} = defineProps<{
    days: Dayjs[];
    /** Members shown as rows (role filter applied), a subset of coverageMembers. */
    members: LegacyAccess[];
    /** Full team: coverage, shortfalls and day totals must ignore the role filter. */
    coverageMembers: LegacyAccess[];
    resolveDay: (member: LegacyAccess, date: Dayjs) => MemberDay;
    targets: StaffingTarget[];
    schedules: Map<number, { weekly_hours: number | null; is_interim: boolean }>;
    /** Date the header filter is focused on, when any. */
    dayFocus: Dayjs | null;
    /** Hour counter synthesis, keyed on the access point. */
    hourBalances: Map<number, TeamHourBalance>;
    /** Opening sheet of the pharmacy, shown as the first row. */
    opening: HealthcenterOpening | null;
    openingState: OpeningState;
    /** Whether the team list loaded, for an empty one to mean an empty team. */
    teamLoaded: boolean;
}>();

const emits = defineEmits<{
    (event: 'edit-range', payload: { day: MemberDay; rangeIndex: number; proposed: TimeRange }): void;
    (event: 'day-click', date: Dayjs): void;
    (event: 'add-member'): void;
    (event: 'open-hour-counter', memberId: number): void;
    (event: 'edit-opening'): void;
}>();

interface Row {
    member: LegacyAccess;
    days: MemberDay[];
    weekMinutes: number;
    role: ReturnType<typeof jobRoleOption>;
    isInterim: boolean;
    balance: TeamHourBalance | null;
}

/** One resolution of the full team per day, shared by every derived metric. */
const resolvedByDay = computed<MemberDay[][]>(() =>
    days.map(date => coverageMembers.map(member => resolveDay(member, date)))
);

const daysByMember = computed<Map<number, MemberDay[]>>(() => {
    const map = new Map<number, MemberDay[]>();

    resolvedByDay.value.forEach(resolved => resolved.forEach(day => {
        map.set(day.member.id, [...(map.get(day.member.id) ?? []), day]);
    }));

    return map;
});

/** A dayFocus from another week resolves to -1 and disables the filter. */
const focusIndex = computed<number>(() =>
    dayFocus ? days.findIndex(date => date.isSame(dayFocus, 'day')) : -1
);

const rows = computed<Row[]>(() => members
    .map(member => {
        const resolved = daysByMember.value.get(member.id) ?? days.map(date => resolveDay(member, date));

        return {
            member,
            days: resolved,
            weekMinutes: resolved.reduce((sum, day) => sum + dayMinutes(day), 0),
            role: jobRoleOption(jobRoleForRank(member.rankId)),
            isInterim: schedules.get(member.id)?.is_interim ?? false,
            balance: hourBalances.get(member.id) ?? null,
        };
    })
    .filter(row => focusIndex.value === -1 || dayMinutes(row.days[focusIndex.value]) > 0)
);

/** Open ranges of the pharmacy per displayed day, empty on a closing day. */
const openingRanges = computed<MinuteRange[][]>(() => days.map(date => openRangesOn(opening, date) ?? []));

const coverages = computed<DayCoverage[]>(() => resolvedByDay.value.map(dayCoverage));

const shortfalls = computed<SlotShortfall[][]>(() =>
    resolvedByDay.value.map((resolved, index) => dayShortfalls(resolved, targets, days[index].day()))
);

const dayHeadcount = (index: number): number => coverages.value[index].headcount;

const dayHours = (index: number): number =>
    Math.round(resolvedByDay.value[index].reduce((sum, day) => sum + dayMinutes(day), 0) / 60);

const balanceTitle = (balance: TeamHourBalance): string =>
    `Heures supplémentaires ${formatHours(balance.overtime_minutes)} · à rattraper ${formatHours(balance.recovery_minutes)}`
    + ' — cliquer pour ouvrir le compteur d\'heures';

/** The tooltip keeps the range readable when the bar is too thin for a label. */
const barTooltip = (row: Row, range: TimeRange): string =>
    `${formatTime(range.starts_at)}–${formatTime(range.ends_at)} · ${row.member.user?.name ?? ''}`;

/**
 * Bar geometry clamped to the displayed window: a range spilling out of
 * 8h–20h must not bleed over the neighbour column, and a range fully
 * outside keeps a thin clickable sliver at the nearest edge.
 */
const barGeometry = (range: TimeRange): { left: number; width: number } => {
    const span = DAY_END - DAY_START;
    const start = Math.min(Math.max(toMinutes(range.starts_at), DAY_START), DAY_END);
    const end = Math.min(Math.max(toMinutes(range.ends_at), DAY_START), DAY_END);
    const width = Math.max((end - start) / span * 100, 2);

    return {left: Math.min((start - DAY_START) / span * 100, 100 - width), width};
};

/** Live edge-resize preview: identifies one bar and its proposed range. */
const resizing = ref<{ memberId: number; dayIndex: number; rangeIndex: number; range: TimeRange } | null>(null);

const displayedRange = (row: Row, dayIndex: number, rangeIndex: number): TimeRange => {
    const preview = resizing.value;

    if (preview && preview.memberId === row.member.id && preview.dayIndex === dayIndex && preview.rangeIndex === rangeIndex) {
        return preview.range;
    }

    return row.days[dayIndex].ranges[rangeIndex];
};

/**
 * Drag one bar edge: 15-minute steps against the column width, then hand the
 * proposed range to the edit dialog — the change stays an exception with a
 * reason, dragging is just a faster way to fill the times in.
 */
let stopResize: (() => void) | null = null;

const startResize = (event: MouseEvent, row: Row, dayIndex: number, rangeIndex: number, edge: 'l' | 'r') => {
    const cell = (event.currentTarget as HTMLElement).closest('[data-day-cell]') as HTMLElement;
    const minutesPerPixel = (DAY_END - DAY_START) / cell.clientWidth;
    const initial = row.days[dayIndex].ranges[rangeIndex];
    const originX = event.clientX;

    resizing.value = {memberId: row.member.id, dayIndex, rangeIndex, range: {...initial}};

    const stop = (commit: boolean) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        stopResize = null;

        const preview = resizing.value;
        resizing.value = null;

        if (commit && preview && (preview.range.starts_at !== initial.starts_at || preview.range.ends_at !== initial.ends_at)) {
            emits('edit-range', {day: row.days[dayIndex], rangeIndex, proposed: preview.range});
        }
    };

    const onMove = (move: MouseEvent) => {
        // A release outside the window never fires mouseup: end the drag on
        // the first move that comes back with the button up.
        if (!(move.buttons & 1)) {
            stop(true);

            return;
        }

        const deltaMinutes = Math.round((move.clientX - originX) * minutesPerPixel / RESIZE_STEP) * RESIZE_STEP;
        const range = {...initial};

        if (edge === 'l') {
            range.starts_at = toTime(Math.min(toMinutes(initial.starts_at) + deltaMinutes, toMinutes(initial.ends_at) - RESIZE_STEP));
        } else {
            range.ends_at = toTime(Math.max(toMinutes(initial.ends_at) + deltaMinutes, toMinutes(initial.starts_at) + RESIZE_STEP));
        }

        resizing.value = {memberId: row.member.id, dayIndex, rangeIndex, range};
    };

    const onUp = () => stop(true);

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    stopResize = () => stop(false);
};

onUnmounted(() => stopResize?.());
</script>

<template>
  <div class="min-w-[1080px] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
    <!-- Day headers -->
    <div class="grid grid-cols-[190px_repeat(7,1fr)] border-b border-gray-200 bg-gray-50">
      <div class="px-3.5 py-2.5 text-[11px] font-semibold text-gray-400">
        Collaborateur
      </div>
      <button
        v-for="(date, index) in days"
        :key="date.valueOf()"
        class="border-l border-gray-200 px-1.5 py-2 text-center transition-colors hover:bg-primary/5"
        :class="dayFocus?.isSame(date, 'day') ? 'bg-primary/10' : ''"
        :title="dayFocus?.isSame(date, 'day') ? 'Toute l\'équipe' : 'Membres présents ce jour uniquement'"
        @click="emits('day-click', date)"
      >
        <div class="text-xs font-semibold capitalize" :class="dayFocus?.isSame(date, 'day') ? 'text-primary' : 'text-gray-700'">
          {{ date.format('ddd') }}
        </div>
        <div class="text-[10px] text-gray-400">
          {{ date.format('DD/MM') }}
        </div>
        <div class="mt-0.5 text-[8.5px] text-gray-400">
          {{ dayHeadcount(index) }} pers · {{ dayHours(index) }} h
        </div>
      </button>
    </div>

    <!-- Pharmacy hours: the frame the team schedules are read against. -->
    <div class="grid grid-cols-[190px_repeat(7,1fr)] border-b border-gray-200 bg-primary/[0.03]">
      <div class="flex flex-col justify-center gap-0.5 px-3.5 py-2">
        <div class="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700">
          <Store class="size-3.5 text-primary" />
          Horaires de la pharmacie
        </div>
        <button
          v-if="openingState === 'filled' || openingState === 'missing'"
          class="self-start text-[10px] font-semibold text-primary hover:underline"
          @click="emits('edit-opening')"
        >
          {{ openingState === 'filled' ? 'Modifier les horaires' : 'Renseigner les horaires' }}
        </button>
      </div>
      <div
        v-for="(ranges, index) in openingRanges"
        :key="index"
        class="flex min-h-[42px] flex-col items-center justify-center gap-0.5 border-l border-gray-100 px-1 py-1.5"
      >
        <Skeleton v-if="openingState === 'loading'" class="h-3 w-3/4" />
        <span v-else-if="openingState === 'failed'" class="text-[9px] text-gray-300">—</span>
        <span
          v-else-if="openingState === 'missing'"
          class="flex w-full items-center justify-center self-stretch rounded-md border border-dashed border-gray-300 text-[9.5px] font-medium text-gray-400"
        >
          Non renseigné
        </span>
        <template v-else-if="ranges.length">
          <span
            v-for="range in ranges"
            :key="range.start"
            class="whitespace-nowrap text-[10px] font-semibold text-gray-700"
          >
            {{ formatMinutes(range.start) }} – {{ formatMinutes(range.end) }}
          </span>
        </template>
        <span v-else class="text-[10px] font-medium text-gray-400">Fermé</span>
      </div>
    </div>

    <!-- Member rows -->
    <div
      v-for="row in rows"
      :key="row.member.id"
      class="grid grid-cols-[190px_repeat(7,1fr)] border-b border-gray-100"
    >
      <div class="flex items-center gap-2.5 px-3.5 py-2">
        <div
          class="flex size-8 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
          :style="{backgroundColor: row.role?.color ?? UNKNOWN_ROLE_COLOR}"
        >
          {{ (row.member.user?.name ?? '?').split(' ').map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase() }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5 truncate text-xs font-semibold text-gray-800">
            {{ row.member.user?.name }}
            <Clock v-if="row.isInterim" class="size-3.5 text-emerald-600" title="Intérimaire" />
            <button
              title="Modifier les horaires de ce membre"
              class="flex size-5 items-center justify-center rounded text-gray-300 hover:bg-primary/10 hover:text-primary"
              @click="$router.push({path: '/team', query: {member: row.member.id}})"
            >
              <Pencil class="size-3" />
            </button>
          </div>
          <div class="flex items-center gap-1.5 text-[10px]">
            <button
              v-if="row.balance?.balance_minutes"
              class="flex-none rounded px-1.5 py-px text-[9px] font-bold"
              :class="row.balance.balance_minutes > 0 ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'"
              :title="balanceTitle(row.balance)"
              @click="emits('open-hour-counter', row.member.id)"
            >
              {{ formatSignedHours(row.balance.balance_minutes) }}
            </button>
            <span
              class="min-w-0 truncate rounded px-1.5 py-px font-semibold"
              :style="{
                backgroundColor: (row.role?.color ?? UNKNOWN_ROLE_COLOR) + '22',
                color: row.role?.color ?? UNKNOWN_ROLE_COLOR
              }"
              :title="row.member.rank?.title ?? row.role?.label ?? ''"
            >
              {{ row.member.rank?.title ?? row.role?.label ?? '—' }}
            </span>
            <span class="shrink-0 whitespace-nowrap text-gray-400">{{ formatHours(row.weekMinutes) }}</span>
          </div>
        </div>
      </div>

      <div
        v-for="(day, dayIndex) in row.days"
        :key="dayIndex"
        data-day-cell
        class="relative min-h-[50px] border-l border-gray-100 p-1"
      >
        <!-- Absence: tinted by its motive, like the timeline and the list.
             A red block read as an error on a maternity leave. -->
        <div
          v-if="day.absence"
          class="absence-hatch absolute inset-1 flex items-center justify-center overflow-hidden rounded-md text-center text-[8.5px] font-semibold leading-tight"
          :class="motifOf(day.absence.reason).classes"
        >
          <span class="relative px-0.5">{{ day.absence.reason }}</span>
        </div>

        <!-- Presence bars -->
        <div v-else-if="day.ranges.length" class="relative h-10">
          <TooltipProvider :delay-duration="150">
            <Tooltip v-for="(range, rangeIndex) in day.ranges" :key="rangeIndex">
              <TooltipTrigger as-child>
                <div
                  class="planning-bar absolute top-1.5 flex h-7 cursor-pointer items-center justify-center overflow-hidden whitespace-nowrap rounded-md px-0.5 text-[9px] font-semibold text-white shadow-sm transition-[filter] hover:brightness-110"
                  :style="{
                    left: `${barGeometry(displayedRange(row, dayIndex, rangeIndex)).left}%`,
                    width: `${barGeometry(displayedRange(row, dayIndex, rangeIndex)).width}%`,
                    backgroundColor: row.role?.color ?? UNKNOWN_ROLE_COLOR
                  }"
                  @click="emits('edit-range', {day, rangeIndex, proposed: displayedRange(row, dayIndex, rangeIndex)})"
                >
                  <span class="planning-bar-label">
                    {{ formatTime(displayedRange(row, dayIndex, rangeIndex).starts_at) }}–{{ formatTime(displayedRange(row, dayIndex, rangeIndex).ends_at) }}
                  </span>
                  <span class="planning-bar-label-short">
                    {{ formatTime(displayedRange(row, dayIndex, rangeIndex).starts_at) }}
                  </span>
                  <span
                    class="absolute inset-y-0 left-0 w-1.5 cursor-ew-resize"
                    @mousedown.prevent.stop="startResize($event, row, dayIndex, rangeIndex, 'l')"
                    @click.stop
                  />
                  <span
                    class="absolute inset-y-0 right-0 w-1.5 cursor-ew-resize"
                    @mousedown.prevent.stop="startResize($event, row, dayIndex, rangeIndex, 'r')"
                    @click.stop
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <div class="font-semibold">
                  {{ barTooltip(row, displayedRange(row, dayIndex, rangeIndex)) }}
                </div>
                <div v-if="day.exception" class="text-amber-300">
                  Exception : {{ day.exception.reason }}
                </div>
                <div class="text-slate-400">
                  Cliquer pour modifier · tirer un bord pour ajuster
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>

    <div v-if="teamLoaded && !coverageMembers.length" class="border-b border-gray-100 px-3.5 py-5 text-center">
      <div class="text-xs font-semibold text-gray-700">
        Aucun collaborateur dans l'équipe
      </div>
      <div class="mt-0.5 text-[11px] text-gray-400">
        Ajoutez les membres de l'équipe pour construire leur planning.
      </div>
    </div>

    <div class="border-b border-gray-100 px-2.5 py-1.5">
      <button
        class="flex h-8 w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/40 text-[11px] font-semibold text-primary hover:bg-primary/5"
        @click="emits('add-member')"
      >
        <UserRoundPlus class="size-3.5" />
        Ajouter une personne à l'équipe
      </button>
    </div>

    <!-- Adequacy row: presence vs static staffing targets, no traffic data. -->
    <div class="grid grid-cols-[190px_repeat(7,1fr)] border-b border-gray-100 bg-gray-50/60">
      <div class="flex items-center gap-1.5 px-3.5 py-2 text-[10.5px] font-semibold text-gray-700">
        <Scale class="size-3.5 text-primary" />
        Effectif vs cible
      </div>
      <div
        v-for="(daySlots, index) in shortfalls"
        :key="index"
        class="flex flex-col items-center justify-center gap-0.5 border-l border-gray-100 px-1 py-1.5"
      >
        <template v-if="targets.some(target => target.day_of_week === days[index].day())">
          <span
            class="flex items-center gap-1 rounded-md px-2 py-0.5 text-[9px] font-bold"
            :class="daySlots.length ? 'bg-amber-50 text-amber-700' : 'bg-green-50 text-green-700'"
          >
            <component :is="daySlots.length ? CircleAlert : CircleCheck" class="size-3" />
            {{ daySlots.length ? `−${Math.max(...daySlots.map(slot => slot.missing))}` : 'OK' }}
          </span>
          <span v-if="daySlots.length" class="text-[8px] text-gray-400">
            {{ formatMinutes(toMinutes(daySlots[0].target.starts_at)) }}–{{ formatMinutes(toMinutes(daySlots[0].target.ends_at)) }}
          </span>
        </template>
        <span v-else class="text-[9px] text-gray-300">—</span>
      </div>
    </div>

    <!-- Pharmacist coverage row -->
    <div class="grid grid-cols-[190px_repeat(7,1fr)] bg-gray-50">
      <div class="flex items-center gap-1.5 px-3.5 py-2 text-[10.5px] font-semibold text-gray-700">
        <ShieldCheck class="size-3.5 text-primary" />
        Couverture pharmacien
      </div>
      <div
        v-for="(coverage, index) in coverages"
        :key="index"
        class="flex flex-col items-center justify-center gap-1 border-l border-gray-100 px-2 py-1.5"
      >
        <div class="relative h-2 w-full overflow-hidden rounded bg-gray-200">
          <div
            v-if="coverage.status !== 'closed'"
            class="absolute inset-y-0 w-full"
            :class="coverage.status === 'covered' ? 'bg-green-500' : coverage.status === 'fragile' ? 'bg-amber-400' : 'bg-red-400'"
          />
        </div>
        <span
          class="flex items-center gap-1 text-[9px] font-semibold"
          :class="coverage.status === 'covered' ? 'text-green-700' : coverage.status === 'fragile' ? 'text-amber-700' : coverage.status === 'uncovered' ? 'text-red-600' : 'text-gray-300'"
        >
          <component :is="coverage.status === 'uncovered' ? ShieldAlert : ShieldCheck" class="size-3" />
          {{ coverage.status === 'covered' ? 'Conforme' : coverage.status === 'fragile' ? 'Fragile' : coverage.status === 'uncovered' ? 'Découvert' : 'Fermé' }}
        </span>
      </div>
    </div>
  </div>

  <!-- Role legend -->
  <div class="mt-3 flex flex-wrap gap-4 text-[11px] text-gray-400">
    <span v-for="role in JOB_ROLES" :key="role.value" class="flex items-center gap-1.5">
      <span class="size-3 rounded" :style="{backgroundColor: role.color}" />
      {{ role.label }}
    </span>
  </div>
</template>

<style scoped>
/*
 * The stripes are drawn in the motive's own text color, so the block keeps
 * the "no work here" texture without a tint hardcoded per motive. Behind the
 * label: both are position-ed at auto z-index, so DOM order decides.
 */
.absence-hatch::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: repeating-linear-gradient(
        45deg,
        currentColor 0,
        currentColor 5px,
        transparent 5px,
        transparent 11px
    );
    opacity: 0.16;
    pointer-events: none;
}

/*
 * Each bar is its own size container so the label adapts to the bar's real
 * pixel width: full range, then start time only, then tooltip only. Old
 * shells whose Chromium ignores container queries keep the full label.
 */
.planning-bar {
    container-type: inline-size;
}

.planning-bar-label-short {
    display: none;
}

@container (max-width: 57px) {
    .planning-bar-label {
        display: none;
    }

    .planning-bar-label-short {
        display: inline;
    }
}

@container (max-width: 19px) {
    .planning-bar-label-short {
        display: none;
    }
}
</style>
